import { useEffect, useRef, useState } from 'react';
import { Button, Select, Stack, Text } from '@mantine/core';
import getTimezone from 'utils/getTimezone';
import {
  coordinateNumber,
  isValidCoordinates,
  sameTimezone,
  timezoneOptions,
} from 'utils/timezones';

type Props = {
  latitude: unknown;
  longitude: unknown;
  value: string | null | undefined;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  autofillOnlyIfEmpty?: boolean;
};

export const SchoolTimezoneInput = ({
  latitude,
  longitude,
  value,
  onChange,
  error,
  disabled,
  autofillOnlyIfEmpty = false,
}: Props) => {
  const lat = coordinateNumber(latitude);
  const lng = coordinateNumber(longitude);
  const location = `${lat},${lng}`;
  const validLocation = isValidCoordinates(lat, lng);
  const [retry, setRetry] = useState(0);
  const [lookup, setLookup] = useState<{
    location: string;
    timezone?: string;
    error?: string;
  }>();
  const appliedLookup = useRef<typeof lookup>();

  useEffect(() => {
    if (!isValidCoordinates(lat, lng)) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void getTimezone(lat, lng).then(
        ({ id }) => {
          if (!cancelled) setLookup({ location, timezone: id });
        },
        (error: unknown) => {
          if (!cancelled)
            setLookup({
              location,
              error:
                error instanceof Error
                  ? error.message
                  : 'Could not verify timezone.',
            });
        },
      );
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [lat, lng, location, retry]);

  const current = lookup?.location === location ? lookup : undefined;
  const suggested = validLocation ? current?.timezone : undefined;
  useEffect(() => {
    if (!current || !suggested || disabled) return;
    if (autofillOnlyIfEmpty && value) return;
    // Apply each resolved location once, even when the previous location filled
    // the field. Later manual choices still receive the usual save validation.
    if (appliedLookup.current === current && value) return;
    appliedLookup.current = current;
    if (!value || !sameTimezone(value, suggested)) onChange(suggested);
  }, [current, suggested, value, disabled, onChange, autofillOnlyIfEmpty]);

  const mismatch = suggested && value && !sameTimezone(value, suggested);
  return (
    <Stack spacing="xs">
      <Select
        label="Timezone"
        placeholder="Select a timezone"
        searchable
        required
        data={timezoneOptions}
        value={value || null}
        onChange={(zone) => onChange(zone || '')}
        disabled={disabled}
        error={
          error || (mismatch ? `This location uses ${suggested}.` : undefined)
        }
      />
      <Text size="sm" role="status" color={current?.error ? 'red' : 'dimmed'}>
        {!validLocation
          ? 'Enter the school’s latitude and longitude to verify its timezone.'
          : current?.error ||
            (suggested
              ? `Timezone at this location: ${suggested}`
              : 'Checking timezone…')}
      </Text>
      {mismatch && (
        <Button
          variant="light"
          disabled={disabled}
          onClick={() => onChange(suggested)}
        >
          Use {suggested}
        </Button>
      )}
      {validLocation && current?.error && (
        <Button
          variant="subtle"
          disabled={disabled}
          onClick={() => {
            setLookup(undefined);
            setRetry((n) => n + 1);
          }}
        >
          Retry timezone lookup
        </Button>
      )}
    </Stack>
  );
};
